import { CheckoutErrorType } from '@/stores/newCheckout'
import { Address } from '@/types/address'

// vicampoErrorCode returned by the API when a purchase order (invoice) is placed with a
// billing country that differs from the shop's country. The API tags it with
// address_type 'billing_address' so it routes to the billing step; the specific server
// message is shown there so the customer knows to use a different billing address (VIP-12386).
export const VICAMPO_ERROR_PURCHASE_ORDER_BILLING_COUNTRY = 1070

// vicampoErrorCode returned by the API when the shipping address is in a country the shop
// does not deliver to. Tagged with address_type 'shipping_address' so it routes to the
// shipping step, where a specific message tells the customer to use a deliverable address
// instead of the generic "error adding address" (VIP-12386).
export const VICAMPO_ERROR_DELIVERY_COUNTRY_NOT_ALLOWED = 1044

/**
 * Ensures address fields are properly formatted for API payload
 * @param addressData - Raw address data from store
 * @returns Formatted address object with all required fields
 */
export function ensureAddressFields(addressData: Address): {
  title: string | null
  first_name: string
  last_name: string
  gender: string | null
  company: string | null
  street_name: string
  street_number: string
  additional: string | null
  zip: string
  city: string
  country: string
  is_default_shipping_address: boolean
  is_default_billing_address: boolean
} {
  return {
    title: addressData.title ?? null,
    first_name: addressData.first_name,
    last_name: addressData.last_name,
    gender: addressData.gender ?? null,
    company: addressData.company ?? null,
    street_name: addressData.street_name,
    street_number: addressData.street_number,
    additional: addressData.additional ?? null,
    zip: addressData.zip,
    city: addressData.city,
    country: addressData.country,
    is_default_shipping_address: false,
    is_default_billing_address: false
  }
}

/**
 * Extracts payment data from payment method object
 * Extracts the nonce. The customer's IP is not added: api-v2 resolves it server-side,
 * and the express order endpoint rejects an ip_address field (VIP-12439)
 * @param data - Payment method data
 * @returns Object with payment_method and braintree_nonce
 */
export function getPaymentData(data: any): {
  payment_method: any
  braintree_nonce?: string
} {
  const paymentMethod = { ...data }

  return {
    payment_method: paymentMethod,
    braintree_nonce: data.tokenized_nonce ?? data.nonce
  }
}

/**
 * Builds base payload for order placement
 * @param cartStore - Cart store instance
 * @param email - Customer email
 * @param subscribeToNewsletter - Whether to subscribe to newsletter
 * @param checkoutType - Provide checkout type identifier for API
 * @param doiOptIn - Whether to record a pending double opt-in consent (VIP-12044)
 * @returns Base payload object
 */
export function buildBasePayload(
  cartStore: any,
  email: string | null | undefined,
  subscribeToNewsletter: boolean | null | undefined,
  checkoutType?: 'guest' | 'express' | 'regular' | 'opc',
  doiOptIn?: boolean | null | undefined
): { [key: string]: any } {
  const payload: { [key: string]: any } = {
    cart_uuid: cartStore.cartId,
    coupon_code: cartStore.storedCouponCode,
    use_credits: cartStore.creditsAreUsed,
    email: email,
    delivery_option_id: cartStore.vicampoDeliveryOptionId,
    miles_spent: cartStore.storedMiles
  }

  if (subscribeToNewsletter) {
    payload.newsletter_subscription = true
  }

  if (doiOptIn) {
    payload.doi_opt_in = true
  }

  if (checkoutType) {
    payload.checkout_type = checkoutType
  }

  return payload
}

/**
 * Handles checkout errors by mapping error causes to CheckoutErrorType
 * and blacklisting payment methods when appropriate
 * @param error - The error object
 * @param useSameBillingAddress - Whether billing address is same as shipping
 * @param blacklistPaymentMethod - Function to blacklist a payment method
 * @param clearPaymentMethod - Function to clear payment method selection
 * @returns CheckoutErrorType that was set
 */
export function handleCheckoutError(
  error: any,
  useSameBillingAddress: boolean | null | undefined,
  blacklistPaymentMethod?: (idOrCode: number | string) => void,
  clearPaymentMethod?: () => void
): CheckoutErrorType {
  // address_type is the authoritative signal from the API for address errors
  if (error.address_type === 'shipping_address') {
    error.description = CheckoutErrorType.SHIPPING_ADDRESS_ERROR
    return CheckoutErrorType.SHIPPING_ADDRESS_ERROR
  }
  if (error.address_type === 'billing_address') {
    error.description = CheckoutErrorType.BILLING_ADDRESS_ERROR
    return CheckoutErrorType.BILLING_ADDRESS_ERROR
  }

  if (error.cause === 301) {
    const isShippingError = useSameBillingAddress === true
    const errorType = isShippingError
      ? CheckoutErrorType.SHIPPING_ADDRESS_ERROR
      : CheckoutErrorType.BILLING_ADDRESS_ERROR
    error.description = errorType
    return errorType
  }

  if (error.cause && error.cause >= 1040 && error.cause < 1050) {
    const errorType =
      error.cause === 1042
        ? CheckoutErrorType.SHIPPING_ADDRESS_ERROR
        : CheckoutErrorType.BILLING_ADDRESS_ERROR
    error.description = errorType
    return errorType
  }

  // Handle cart ownership errors (cart doesn't belong to user)
  if (error.cause === 1050) {
    const errorType = CheckoutErrorType.CART_OWNERSHIP_ERROR
    error.description = errorType
    return errorType
  }

  if (error.cause || error.status === 499 || error.status === 400) {
    const errorType = CheckoutErrorType.PAYMENT_METHOD_ERROR
    error.validationMessage = error.description || errorType
    error.description = errorType

    // Blacklist payment method if callback provided
    if (blacklistPaymentMethod && clearPaymentMethod) {
      blacklistPaymentMethod(error.paymentMethodId || error.paymentMethodCode)
      clearPaymentMethod()
    }

    return errorType
  }

  const errorType = CheckoutErrorType.FALLBACK_CREATE_ORDER_ERROR
  error.description = errorType
  return errorType
}
